<script>
import { mapState, mapActions } from 'vuex'
import graphql from '@/api/graphql'

import Card from '@/components/Card-v2'
import DataTable from '@/components/DataTable'
import { CheckboxComponent } from 'vue-elder-checkbox'
import EmptyState from '@/components/EmptyState'
import Status from './StatusBadge.vue'

export default {
  props: {
    title: {
      type: String,
      default() {
        return this.$tc('contract', 2)
      },
    },
    contact: {
      type: String,
    },
    reference: {
      type: Object,
      required: true,
    },
    defaultStatusFilter: {
      type: Array,
      default: () => ['!draft', '!declined', '!unsigned'],
    },
  },
  watch: {
    filterComp: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      promises: { fetch: null },
      items: [],
      filter: {
        status: this.defaultStatusFilter,
      },
      columns: [
        { id: 'title', label: this.$tc('document', 1) },
        { id: 'status', label: this.$t('status') },
        { id: 'type', label: this.$tc('type', 1) },
      ],
    }
  },
  computed: {
    filterComp() {
      return {
        ...this.filter,
        references: [this.reference],
      }
    },
    showDeclinedContracts: {
      get() {
        return !this.filter.status.includes('!declined')
      },
      set(val) {
        if (val) this.filter.status = this.filter.status.filter(e => e !== '!declined')
        else this.filter.status.push('!declined')
      },
    },
  },
  methods: {
    fetch() {
      this.promises.fetch = graphql
        .request(`query($filter: ContractFilter) { Contracts(filter: $filter) { id, title, status, type } }`, {
          filter: this.filterComp,
        })
        .then(data => (this.items = data.Contracts))
    },
    getTypeLabel(type) {
      switch (type) {
        case 'purchase':
        case 'bid':
        case 'shop':
          return `${type}Contract`
        case 'shop-partial':
          return 'shopContract'
        default:
          return 'contract'
      }
    },
  },
  components: {
    DataTable,
    EmptyState,
    Card,
    Status,
    CheckboxComponent,
  },
}
</script>

<template>
  <Card class="contract-list">
    <template #header>
      <div class="card-v2__title">{{ title }}</div>
      <div class="contract-list__actions">
        <CheckboxComponent v-model="showDeclinedContracts" :label="$t('showDeclinedContracts')" />
        <ButtonComponent
          v-if="contact"
          :label="$t('create')"
          icon="fa-pro-light:plus"
          tag="router-link"
          theme="primary"
          :to="{ name: 'custom-contract-create', query: { contact } }"
        />
      </div>
    </template>
    <template #default>
      <Loader :value="promises.fetch" :message="`${$t('loading', { resource: $tc('contract', 2).toLowerCase() })}...`">
        <DataTable
          sticky
          theme="border"
          :columns="columns"
          :value="items"
          :row-wrapper="
            item => ({
              component: 'router-link',
              props: { to: { name: 'contract', params: { id: item.id } } },
            })
          "
        >
          <template #status="{ item }">
            <Status :value="item.status" />
          </template>
          <template #title="{ item }">
            {{ item.title }}
          </template>
          <template #type="{ item }">
            {{ $tc(getTypeLabel(item.type)) }}
          </template>
        </DataTable>
        <EmptyState v-if="!items.length" :icon="['fal', 'pen-sign']" :title="$t('noContracts')" />
      </Loader>
    </template>
  </Card>
</template>

<style lang="scss">
.contract-list {
  .card-v2__header {
    padding-bottom: 0;

    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__actions {
    display: flex;
    gap: 1.5rem;
    align-items: center;
  }
}
</style>
